// =============================
// Imports
// =============================

import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import { compose } from 'redux';
import { withTheme } from 'styled-components';
import { ConditionalWrapper, WithCustomTheme } from '@mewo/components';
import _get from 'lodash/get';

import withResponsive from '../../hoc/withResponsive';

import { Desktop } from '../../other/responsive';
import EntityActions from '../../other/entityActions';
import MusicItem from '../../containers/musicitem';
import PageWrapper from '../../other/pagewrapper';

import * as playerCtx from '../../../store/constants/PlayerContexts';

import {
  presentShortDescription,
  isDescriptionTooLong,
  getTenant,
  presentDurationEntity,
  presentTags,
} from '../../../helpers/entity/common';
import {
  getPlaylistTitle,
  getPlaylistCoverUrl,
  getPlaylistTracksCoversUrl,
} from '../../../helpers/entity/playlist';
import * as pth from '../../../helpers/proptypes';
import { getLinkRouteProps } from '../../../helpers/link';

import {
  Title,
  Name,
  SubName,
  SubNameTitle,
  EntityDesc,
  StyledMusicItemOptionsToggler,
  EntityCover,
  Informations,
  DescSeeMore,
  TagsList,
  StyledTag,
  Duration,
  Breadcrumb,
} from '../entity.styles';
import { Header, WideContainer, WideBackground } from './styles';

// =============================
// Component
// =============================

class Playlist extends Component {
  static propTypes = {
    getTracks: PropTypes.func.isRequired,
    /** If True, has access to website when private. */
    hasAccess: PropTypes.bool,
    hasMoreTracks: PropTypes.bool.isRequired,
    /** If True, user is on a mobile device. */
    isMobile: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    openPlaylistDescModal: PropTypes.func.isRequired,
    playlist: pth.playlist.isRequired,
    t: PropTypes.func.isRequired,
    tags: PropTypes.arrayOf(pth.tagWithCat).isRequired,
    theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    tracks: PropTypes.arrayOf(pth.track).isRequired,
    parentPage: pth.baseConfigMenu,
  };

  static defaultProps = {
    hasAccess: true,
    theme: null,
    parentPage: [],
  };

  render() {
    const {
      getTracks,
      hasAccess,
      hasMoreTracks,
      isMobile,
      isLogged,
      locale,
      openPlaylistDescModal,
      playlist,
      t,
      tags,
      theme,
      tracks,
      parentPage,
    } = this.props;

    const playlistCover = getPlaylistCoverUrl(playlist, 'original');

    const wideBackground = playlistCover || null;

    const displayTags = presentTags(
      _get(playlist, 'tags', [])
        .map(({ id }) => tags.find(tag => tag.id === id)),
      locale,
    );

    return (
      <InfiniteScroll
        hasMore={hasMoreTracks}
        initialLoad={!isMobile}
        loadMore={getTracks}
        threshold={250}
        useWindow={false}
      >
        <PageWrapper type="playlist" playlistWithoutAccess={!hasAccess}>
          <WithCustomTheme
            customTheme={baseTheme => (baseTheme.displayConf.playlistImg === 'wide'
              ? {
                colors: baseTheme.colors.pages.playlistWide,
              }
              : {})}
          >
            {parentPage[0] && (
              <Breadcrumb {...getLinkRouteProps(parentPage[0], isLogged)}>
                {t('pages:entity.all_playlists')}
              </Breadcrumb>
            )}
            <Header>
              {theme.displayConf.playlistImg === 'wide' && (
                <WideBackground
                  wideBackground={wideBackground}
                  alt={getPlaylistTitle(playlist)}
                />
              )}
              {theme.displayConf.playlistImg === 'cover' && (
                <EntityCover
                  placeholderType="playlist"
                  type="playlist"
                  src={
                    getPlaylistCoverUrl(playlist, 'large')
                    || getPlaylistTracksCoversUrl(playlist, 'large')
                  }
                  lazyload={false}
                  alt={getPlaylistTitle(playlist)}
                />
              )}
              <Informations>
                <Name>{getPlaylistTitle(playlist)}</Name>

                <SubName>
                  {t('pages:entity.playlist_by')}
                  &nbsp;
                  <SubNameTitle>{getTenant(playlist)}</SubNameTitle>
                  &nbsp;-&nbsp;
                  {new Date(_get(playlist, 'createdAt')).getFullYear()}
                </SubName>

                {displayTags.length > 0 && (
                  <TagsList>
                    {displayTags
                      .slice(0, 8)
                      .map(tag => (
                        <StyledTag key={tag.id} data={tag} />
                      ))}
                  </TagsList>
                )}

                {presentShortDescription(_get(playlist, 'descriptions', []), locale) && (
                  <EntityDesc>
                    {presentShortDescription(_get(playlist, 'descriptions', []), locale)}
                    {isDescriptionTooLong(_get(playlist, 'descriptions', []), locale) && (
                      <DescSeeMore onClick={openPlaylistDescModal}>
                        {t('pages:entity.see_more')}
                      </DescSeeMore>
                    )}
                  </EntityDesc>
                )}

                <EntityActions
                  doc={playlist}
                  type="playlist"
                  options={{
                    addToPlaylist: true,
                    downloadArchive: true,
                    share: true,
                    duplicatePlaylist: true,
                    downloadCopyright: true,
                  }}
                  hasTracks={!!tracks.length}
                  playerContext={playerCtx.PLAYLIST}
                />
              </Informations>
            </Header>
          </WithCustomTheme>

          <ConditionalWrapper
            condition={theme.displayConf.playlistImg === 'wide'}
            Wrapper={<WideContainer />}
          >
            {tracks.length > 0 && (
              <Fragment>
                <Title>
                  <div>
                    {_get(playlist, 'nbTracks')}
                    &nbsp;
                    {t('pages:entity.track', { count: _get(playlist, 'nbTracks', 0) })}
                    <Desktop>
                      &nbsp;
                      <Duration inline>
                        {`(${t('pages:entity.duration')}: ${presentDurationEntity(
                          _get(playlist, 'duration', 0),
                        )})`}
                      </Duration>
                    </Desktop>
                  </div>
                  <StyledMusicItemOptionsToggler />
                </Title>
                {tracks.map((e, i) => (
                  <MusicItem
                    key={e.id}
                    music={e}
                    contextName={playerCtx.PLAYLIST}
                    contextId={playlist.id}
                    contextPosition={i + 1}
                  />
                ))}
              </Fragment>
            )}
          </ConditionalWrapper>
        </PageWrapper>
      </InfiniteScroll>
    );
  }
}

export default compose(
  withTheme,
  withResponsive,
)(Playlist);
